import { useState } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowUpRight, TrendingUp } from "lucide-react";
import SectionHeader from "@/components/SectionHeader";
import { PORTFOLIO, PORTFOLIO_FILTERS } from "@/data/site";

export default function PortfolioGrid({ withFilters = false, limit, showHeader = true }) {
  const [filter, setFilter] = useState("All");
  let items = filter === "All" ? PORTFOLIO : PORTFOLIO.filter((p) => p.category === filter);
  if (limit) items = items.slice(0, limit);

  return (
    <section className="dq-container py-20 lg:py-28" data-testid="portfolio-section" id="portfolio">
      {showHeader && (
        <div className="mb-10 flex flex-col items-start justify-between gap-6 lg:flex-row lg:items-end">
          <SectionHeader
            eyebrow="Selected work"
            title="Results we're"
            highlight="proud of"
            subtitle="A glimpse of the premium digital experiences and growth we've delivered."
          />
          {!withFilters && (
            <Link to="/portfolio" data-testid="portfolio-view-all" className="btn-secondary shrink-0">
              View full portfolio <ArrowUpRight className="h-4 w-4" />
            </Link>
          )}
        </div>
      )}

      {withFilters && (
        <div className="mb-10 flex flex-wrap gap-2" data-testid="portfolio-filters">
          {PORTFOLIO_FILTERS.map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              data-testid={`portfolio-filter-${f.toLowerCase().replace(/\s/g, "-")}`}
              className={`rounded-full px-5 py-2 text-sm font-semibold transition-colors duration-200 ${
                filter === f ? "bg-dq-ink text-white" : "border border-dq-line bg-white text-dq-muted hover:text-dq-ink"
              }`}
            >
              {f}
            </button>
          ))}
        </div>
      )}

      <motion.div layout className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        <AnimatePresence mode="popLayout">
          {items.map((p, i) => (
            <motion.article
              key={p.title}
              layout
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: (i % 3) * 0.08 }}
              className={`group relative overflow-hidden rounded-3xl border border-dq-line bg-white ${
                i % 5 === 0 ? "sm:col-span-2 lg:col-span-1" : ""
              }`}
              data-testid={`portfolio-card-${i}`}
            >
              <div className="relative aspect-[4/3] overflow-hidden">
                <img src={p.image} alt={`${p.title} — ${p.tag}`} loading="lazy"
                  className="h-full w-full object-cover transition-transform duration-700 ease-out group-hover:scale-110" />
                <div className="absolute inset-0 bg-gradient-to-t from-dq-ink/70 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                <span className="absolute left-4 top-4 rounded-full bg-white/90 px-3 py-1 text-xs font-semibold text-dq-ink backdrop-blur">
                  {p.category}
                </span>
              </div>
              <div className="p-6">
                <p className="text-xs font-semibold uppercase tracking-wider text-dq-orange">{p.tag}</p>
                <h3 className="mt-2 font-display text-xl font-bold text-dq-ink">{p.title}</h3>
                <div className="mt-3 flex items-center gap-2 text-sm font-semibold text-dq-success">
                  <TrendingUp className="h-4 w-4" /> {p.result}
                </div>
                <Link to="/case-studies" data-testid={`portfolio-case-${i}`}
                  className="link-underline mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-dq-ink">
                  View case study <ArrowUpRight className="h-4 w-4" />
                </Link>
              </div>
            </motion.article>
          ))}
        </AnimatePresence>
      </motion.div>
    </section>
  );
}
